/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  updateDataForTable,
  updateDataForCheckbox,
  updateDataForPercentageRow,
  updateDataForSelectedBoolean
} from 'src/components/collapsible-table/table-data-prep';
import { IDynamicFormValue } from 'src/lib';
import {
  fetchSummaryData,
  fetchPotentialRiskData,
  fetchPPGLevelImpactData,
  fetchMultiyearEstimateData,
  fetchResetMultiyearEstimate,
  saveMultiyearSimulation,
  savePotentialRisksUpsides,
  fetchSummaryDownloadData,
  fetchCurrencyToggledata,
  setToggleCurrencystate,
} from '../api/simulation-summary-api';
import { ISimulationSummaryState } from '../types/simulation-summary.model';
import { CurrencyToggleItem } from '../types/simulation-summary.model';
import {
  createColumnVisibilityStateForOutputColumns,
  filterColumnsForToggleView
} from '../../pricing-simulator/utils/column-utils';
import {
  Header,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';
import { IDataRow } from '../../pricing-simulator/types/simulation-view-table.model';

// slice to get summary data
const getSummaryData = createAsyncThunk(
  'getSummaryData',
  async (args: {
    source_id: number;
    module_id: number;
    scenario_id: number;
    filter_inputs: any;
    currency_toggle?: boolean;
  }) => {
    const response: any = await fetchSummaryData(
      args.source_id,
      args.scenario_id,
      args.module_id,
      args.filter_inputs,
      args.currency_toggle
    );
    return response;
  }
);

// slice to get potential risk data
const getPotentialRiskData = createAsyncThunk(
  'getPotentialRiskData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    dataInput: any;
  }) => {
    const response = await fetchPotentialRiskData(
      args.dataInput,
      args.sourceId,
      args.moduleId,
      args.scenarioId
    );
    return response;
  }
);

// slice to get impact at ppg level data
const getPPGLevelImpactData = createAsyncThunk(
  'getPPGLevelImpactData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    dataInput: any;
    currency_toggle: boolean;
  }) => {
    const response = await fetchPPGLevelImpactData(
      args.dataInput,
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      args.currency_toggle
    );
    return response;
  }
);

// slice to get multiyear estimate data
const getMultiyearEstimateData = createAsyncThunk(
  'getMultiyearEstimateData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    dataInput: any;
  }) => {
    const response = await fetchMultiyearEstimateData(
      args.dataInput,
      args.sourceId,
      args.moduleId,
      args.scenarioId
    );
    return response;
  }
);

const resetMultiyearEstimate = createAsyncThunk(
  'getMultiyearEstimateData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    dataInput: any;
  }) => {
    const response = await fetchResetMultiyearEstimate(
      args.dataInput,
      args.sourceId,
      args.moduleId,
      args.scenarioId
    );
    return response;
  }
);

const getCurrencyToggledata = createAsyncThunk(
  'getCurrencyToggledata',
  async (args: {
    sourceId: number;
    scenarioId: number;
  }) => {
    const response = await fetchCurrencyToggledata(
      args.sourceId,
      args.scenarioId
    );
    return response.toggle;
  }
);

const toggleCurrency = createAsyncThunk(
  'toggleCurrency',
  async ({
    sourceId,
    scenarioId,
    isTargetEnabled
  }: {
    sourceId: number;
    scenarioId: number;
    isTargetEnabled: boolean;
  }) => {
    const response = await setToggleCurrencystate(
      sourceId,
      scenarioId,
      isTargetEnabled

    );
    return response.data;
  }
)

const saveMultiyearSimulationData = createAsyncThunk(
  'saveMultiyearSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const response = await saveMultiyearSimulation(
      sourceId,
      moduleId,
      scenarioId
    );
    return response;
  }
);

const savePotentialRisksUpsidesData = createAsyncThunk(
  'saveMultiyearSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const response = await savePotentialRisksUpsides(
      sourceId,
      moduleId,
      scenarioId
    );
    return response;
  }
);

export const downloadSimulationSummaryData = createAsyncThunk(
  'downloadSimulationSummaryData',
  async (
    args: {
      scenarioId: number;
      moduleId: number;
      sourceId: number;
    },
    thunkAPI
  ) => {
    const scenarioName =
      (thunkAPI.getState() as any)?.simulatorHome?.currentScenario?.name ||
      'Summary';
    const response = await fetchSummaryDownloadData(
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      scenarioName
    );
    return response;
  }
);

const initialState: ISimulationSummaryState = {
  isSimulating: false,
  fetchingMetaData: false,
  fetchingSummaryData: false,
  fetchingPotentialRiskData: false,
  fetchingMultiyearEstimateData: false,
  currencyToggle: [],
  isTargetEnabled: false,
  inputCols: [],
  potentialRiskData: {},
  multiyearEstimateData: {
    inputCols: [],
    resultCols: []
  },
  multiyearToggle: 'perc',
  multiyearColumnVisibiltyState: {},
  simulatedRow: [],
  isScenarioSaving: false,
  isScenarioSavedSuccessfully: false
};

const simulationSummarySlice = createSlice({
  name: 'simulation-summary',
  initialState,
  reducers: {
    handleMultiyearToggleChange(
      state,
      action: PayloadAction<{
        multiyearToggle: string;
      }>
    ) {
      state.multiyearToggle = action.payload.multiyearToggle;
      state.multiyearColumnVisibiltyState =
        createColumnVisibilityStateForOutputColumns(
          [
            ...state.multiyearEstimateData.inputCols,
            ...state.multiyearEstimateData.resultCols
          ],
          state.multiyearToggle
        );
    },
    handleMultiyearPercInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const updatedColumnData = updateDataForTable(
        state.multiyearEstimateData,
        action
      );
      state.multiyearEstimateData.row = updatedColumnData;
    },
    handleMultiyearEstimateInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const updatedColumnData = updateDataForTable(
        state.multiyearEstimateData,
        action
      );
      state.multiyearEstimateData.row = updatedColumnData;
    },
    handleMultiyearEstimateCheckboxChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const updatedColumnData = updateDataForCheckbox(
        state.multiyearEstimateData,
        action
      );
      state.multiyearEstimateData.row = updatedColumnData;
    },
    handleMultiyearSimulationFlagValue(state) {
      state.isSimulating = true;
    },
    handleMultiyearPercOrAdditionalValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
        dataRow: IDataRow;
      }>,
      uniqueId = 'cell_id'
    ) {
      const rowStateData = [
        state.multiyearEstimateData.row[action.payload.dataRow.index]
          ? state.multiyearEstimateData.row[action.payload.dataRow.index]
          : state.multiyearEstimateData.row[action.payload.dataRow.parent_index]
      ];
      updateDataForPercentageRow(
        state.multiyearEstimateData,
        rowStateData,
        uniqueId,
        action
      );
    },
    handleNewMultiyearValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
        dataRow: IDataRow;
      }>,
      uniqueId = 'cell_id'
    ) {
      const rowStateData = [
        state.multiyearEstimateData.row[action.payload.dataRow.index]
          ? state.multiyearEstimateData.row[action.payload.dataRow.index]
          : state.multiyearEstimateData.row[action.payload.dataRow.parent_index]
      ];
      updateDataForPercentageRow(
        state.multiyearEstimateData,
        rowStateData,
        uniqueId,
        action
      );
    },
    handleMultiyearBooleanChange(
      state,
      action: PayloadAction<{
        columnMetadata: Header;
        newValue: number;
        dataRow: IDataRow;
        isRowCanExpand: boolean;
      }>
    ) {
      const updatedColumnData = updateDataForSelectedBoolean(
        state.multiyearEstimateData,
        action,
        'cell_id'
      );
      state.multiyearEstimateData.row = updatedColumnData;
    },
    handleValueOfSavedScenario(state) {
      state.isScenarioSavedSuccessfully = false;
    },
    setCurrencyToggleData: (state, action: PayloadAction<CurrencyToggleItem[]>) => {
      state.currencyToggle = action.payload;
    },
    handleCurrencyToggleState(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isTargetEnabled = action.payload;
    },

  },
  extraReducers: (builder) =>
    builder
      .addCase(getCurrencyToggledata.fulfilled, (state, action) => {
        state.currencyToggle = action.payload;
      })
      .addCase(toggleCurrency.fulfilled, (state, action) => {
        state.isTargetEnabled = action.payload.is_target_enabled;
      })
      .addCase(getPotentialRiskData.pending, (state) => {
        state.fetchingPotentialRiskData = true;
      })
      .addCase(getPotentialRiskData.fulfilled, (state, action) => {
        state.fetchingPotentialRiskData = false;
        state.potentialRiskData = action.payload;
      })
      .addCase(getPotentialRiskData.rejected, (state) => {
        state.fetchingPotentialRiskData = false;
      })
      .addCase(getMultiyearEstimateData.pending, (state) => {
        state.fetchingMultiyearEstimateData = true;
        state.multiyearEstimateData = {
          inputCols: [],
          resultCols: []
        };
      })
      .addCase(getMultiyearEstimateData.fulfilled, (state, action) => {
        state.fetchingMultiyearEstimateData = false;
        state.multiyearEstimateData = action.payload;
        // cloning multiyear estimate data
        let multiyearEstimateClonedData = JSON.parse(
          JSON.stringify(state.multiyearEstimateData)
        );
        // let multiyearEstimateClonedData = [...state.multiyearEstimateData];
        // upating the inputs column of multiyear estimate cloned data
        multiyearEstimateClonedData['inputCols'] = filterColumnsForToggleView(
          [TypeOfTableData.DISPLAY, TypeOfTableData.INPUT],
          action.payload.header,
          ''
        );
        // updating the state of multiyear estimate data
        state.multiyearEstimateData = multiyearEstimateClonedData;
        // state.multiyearEstimateData.inputCols = filterColumnsForToggleView(
        //   [TypeOfTableData.DISPLAY, TypeOfTableData.INPUT],
        //   action.payload.header,
        //   ''
        // );
        state.multiyearEstimateData.resultCols = filterColumnsForToggleView(
          [TypeOfTableData.OUTPUT],
          action.payload.header,
          ''
        );
        state.multiyearColumnVisibiltyState =
          createColumnVisibilityStateForOutputColumns(
            [
              ...state.multiyearEstimateData.inputCols,
              ...state.multiyearEstimateData.resultCols
            ],
            state.multiyearToggle
          );
      })
      .addCase(getMultiyearEstimateData.rejected, (state) => {
        state.fetchingMultiyearEstimateData = false;
        state.multiyearEstimateData = {
          inputCols: [],
          resultCols: []
        };
      })
      .addCase(saveMultiyearSimulationData.pending, (state) => {
        state.isScenarioSaving = true;
        state.isScenarioSavedSuccessfully = false;
      })
      .addCase(saveMultiyearSimulationData.fulfilled, (state) => {
        state.isScenarioSaving = false;
        state.isScenarioSavedSuccessfully = true;
      })
      .addCase(saveMultiyearSimulationData.rejected, (state) => {
        state.isScenarioSaving = false;
      })
});

export const {
  handleMultiyearToggleChange,
  handleMultiyearPercInputChange,
  handleMultiyearEstimateInputChange,
  handleMultiyearEstimateCheckboxChange,
  handleMultiyearSimulationFlagValue,
  handleMultiyearPercOrAdditionalValueChange,
  handleNewMultiyearValueChange,
  handleMultiyearBooleanChange,
  handleValueOfSavedScenario,
  setCurrencyToggleData,
  handleCurrencyToggleState
} = simulationSummarySlice.actions;

export {
  getSummaryData,
  getPotentialRiskData,
  getPPGLevelImpactData,
  getMultiyearEstimateData,
  resetMultiyearEstimate,
  saveMultiyearSimulationData,
  savePotentialRisksUpsidesData,
  getCurrencyToggledata,
  toggleCurrency
};

export default simulationSummarySlice;